import type { ImageProps as UdsImageProps } from '@sunwebgroup/uds-ui/image'
import type { BaseHTMLAttributes, ImgHTMLAttributes, JSX } from 'react'
import { Icon } from '@sunwebgroup/uds-ui/icon'
import { LazyImage as UdsLazyImage } from '@sunwebgroup/uds-ui/lazy-image'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import cn from 'classnames'
import { useEffect, useState } from 'react'
import { useMountedState } from 'react-use'
import { buildImgSrc, buildImgSrcSet } from '@/helpers/image-url'
import styles from './Image.module.css'

interface ImageComponentProps extends Omit<UdsImageProps, 'srcSet'> {
  src: string
  srcSet?: Array<number>
}

const defaultSrcSet: number[] = []

const DEFAULT_PLACEHOLDER_IMAGE_WIDTH = 20

/**
 * @deprecated Use MediaServerLazyImage instead
 */
export function Image({ srcSet = defaultSrcSet, src, ...props }: ImageComponentProps) {
  const isMounted = useMountedState()

  if (!isMounted)
    return null

  return (
    <UdsLazyImage
      src={srcSet.length > 0 ? buildImgSrc(src, srcSet[0]) : src}
      srcSet={srcSet.length > 0 ? buildImgSrcSet(src, srcSet) : undefined}
      placeholderSrc={buildImgSrc(src, DEFAULT_PLACEHOLDER_IMAGE_WIDTH)}
      {...props}
    />
  )
}

Image.displayName = 'Image'

export default Image

/**
 * @deprecated Use MediaServerLazyImage instead
 */
export interface ImageProps extends Pick<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'className' | 'loading' | 'alt'> {
  sizes?: string
  src: string
  srcSet?: number[]
}

interface ImageFallbackProps extends BaseHTMLAttributes<HTMLDivElement> {
  alt?: string
}

/**
 * @deprecated Use MediaServerLazyImage instead
 */
export function LazyImagePlaceholder({ className, alt, ...props }: ImgHTMLAttributes<HTMLImageElement>) {
  return <img className={cn(styles.lazyImagePlaceholder, className)} alt={alt ?? ''} {...props} />
}

/**
 * @deprecated Try using MediaServerLazyImage instead
 * In case you need to add custom styles, and need to extend Image, make sure you understand how UDS LazyImage works internally
 */
export function ImageWrapper({ className, ...props }: BaseHTMLAttributes<HTMLDivElement>) {
  return <div data-image-wrapper className={cn(styles.imageWrapper, className)} {...props} />
}

/**
 * @deprecated Use MediaServerLazyImage instead
 */
export function LazyImage({ className, ...props }: ImageProps) {
  return <LegacyImage className={cn(styles.lazyImage, className)} {...props} />
}

/**
 * @deprecated Use MediaServerLazyImage instead
 */
export function LazyImageFallback(props: ImageFallbackProps) {
  return <ImageFallback {...props} />
}

function LegacyImage({ sizes, src, srcSet = [], className, ...attributes }: ImageProps) {
  const [imageError, setImageError] = useState(false)
  const [imageIsLoaded, setImageIsLoaded] = useState(false)

  useEffect(() => {
    setImageError(false)
    setImageIsLoaded(false)
  }, [src])

  if (imageError) {
    return (
      <ImageWrapper>
        <LazyImageFallback alt={attributes.alt} />
      </ImageWrapper>
    )
  }

  return (
    <ImageWrapper>
      <LazyImagePlaceholder
        {...attributes}
        className={className}
        alt={attributes.alt ?? ''}
        src={buildImgSrc(src, 20)}
        loading="eager"
        onErrorCapture={() => {
          setImageError(true)
        }}
      />
      <img
        {...attributes}
        className={cn(styles.image, imageIsLoaded ? styles.imageLoaded : styles.imageNotLoaded, className)}
        alt={attributes.alt ?? ''}
        onErrorCapture={() => setImageError(true)}
        onLoadCapture={() => setImageIsLoaded(true)}
        sizes={sizes}
        src={srcSet.length > 0 ? buildImgSrc(src, srcSet[0]) : src}
        srcSet={srcSet.length > 0 ? buildImgSrcSet(src, srcSet) : undefined}
      />
    </ImageWrapper>
  )
}

LegacyImage.displayName = 'LazyImage'

function ImageFallback({ alt, className, ...attributes }: ImageFallbackProps): JSX.Element {
  return (
    <div className={cn(styles.error, className)} {...attributes}>
      <Icon glyph="ImageSlashed" size={32} />
      <Typography className={styles.errorText} variant="body-s-regular-tight">{alt}</Typography>
    </div>
  )
}
